import { UPGRADE_DEFS, STAT_UPGRADE_DEFS, STAT_MAX_LEVEL, xpForLevel, type StatKey } from '../shared';
import type { ColyseusClient } from '../network/ColyseusClient';

export class HUD {
  private crystalCount = document.getElementById('crystal-count')!;
  private levelDisplay = document.getElementById('level-display')!;
  private healthBarFill = document.getElementById('health-bar-fill') as HTMLDivElement;
  private xpBarFill = document.getElementById('xp-bar-fill') as HTMLDivElement;
  private killFeed = document.getElementById('kill-feed')!;
  private levelupBanner = document.getElementById('levelup-banner')!;
  private levelupSub = document.getElementById('levelup-sub')!;
  private upgradeMenu = document.getElementById('upgrade-menu')!;
  private upgradeContent = document.getElementById('upgrade-content')!;
  private upgradeCrystalBalance = document.getElementById('upgrade-crystal-balance')!;
  private upgradeBtn = document.getElementById('upgrade-btn')!;
  private upgradeClose = document.getElementById('upgrade-close')!;
  private killsDisplay = document.getElementById('kills-display')!;
  private statsBtn = document.getElementById('stats-btn')!;
  private statsPopup = document.getElementById('stats-popup')!;
  private deathScreen: HTMLDivElement;
  private hitFlash: HTMLDivElement;
  private hitFlashTimer = 0;
  private killBanner: HTMLDivElement;

  private crystals = 0;
  private level = 1;
  private xp = 0;
  private prevHp = -1;
  private prevMaxHp = -1;
  private prevKills = -1;
  private prevDeaths = -1;
  private ownedUpgrades = { weapon: 0, chassis: 0, movement: 0, attachment: null as string | null };
  private statLevels: Record<string, number> = {};
  private confirmedStatLevels: Record<string, number> = {};
  private pendingUpgrades = new Set<string>();
  private network: ColyseusClient | null = null;

  constructor() {
    // Death / respawn overlay
    this.deathScreen = document.createElement('div');
    this.deathScreen.id = 'death-screen';
    this.deathScreen.style.cssText = [
      'position:fixed', 'inset:0',
      'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center',
      'z-index:200', 'pointer-events:none',
      'opacity:0', 'transition:opacity 0.25s',
      'background:radial-gradient(ellipse at center, rgba(180,0,0,0.55) 0%, rgba(60,0,0,0.75) 100%)',
    ].join(';');
    this.deathScreen.innerHTML = `
      <div style="color:#ff2020;font-size:clamp(48px,8vw,88px);font-weight:900;letter-spacing:0.14em;text-shadow:0 0 40px rgba(255,0,0,0.9),0 0 80px rgba(255,0,0,0.5);font-family:inherit">DESTROYED</div>
      <div style="color:rgba(255,180,180,0.75);font-size:clamp(13px,2vw,20px);margin-top:14px;letter-spacing:0.25em;text-transform:uppercase;font-family:inherit">Respawning&hellip;</div>
    `;
    document.body.appendChild(this.deathScreen);

    // Damage vignette — red border flash scaled to damage severity
    this.hitFlash = document.createElement('div');
    this.hitFlash.style.cssText = [
      'position:fixed', 'inset:0',
      'pointer-events:none', 'z-index:190',
      'opacity:0', 'transition:opacity 0.06s ease-in',
      'background:radial-gradient(ellipse at center, transparent 35%, rgba(210,0,0,0.72) 100%)',
    ].join(';');
    document.body.appendChild(this.hitFlash);

    // Kill notification banner
    this.killBanner = document.createElement('div');
    this.killBanner.style.cssText = [
      'position:fixed', 'top:38%', 'left:50%',
      'transform:translateX(-50%) scale(0.8)',
      'pointer-events:none', 'z-index:195',
      'text-align:center',
      'opacity:0',
      'transition:opacity 0.12s ease-out, transform 0.12s ease-out',
    ].join(';');
    this.killBanner.innerHTML = `
      <div style="color:#ff6600;font-size:clamp(28px,5vw,52px);font-weight:900;letter-spacing:0.18em;text-shadow:0 0 20px rgba(255,100,0,0.9),0 0 40px rgba(255,60,0,0.5);font-family:inherit;line-height:1">ELIMINATED</div>
      <div id="kill-banner-name" style="color:rgba(255,200,150,0.85);font-size:clamp(11px,1.8vw,18px);margin-top:6px;letter-spacing:0.22em;text-transform:uppercase;font-family:inherit"></div>
    `;
    document.body.appendChild(this.killBanner);

    this.upgradeBtn.addEventListener('click', () => this.toggleUpgradeMenu());
    this.upgradeClose.addEventListener('click', () => this.closeUpgradeMenu());
    this.upgradeMenu.addEventListener('click', (e) => {
      if (e.target === this.upgradeMenu) this.closeUpgradeMenu();
    });
    this.statsBtn.addEventListener('click', () => this.toggleStatPanel());
    document.addEventListener('keydown', (e) => {
      if (e.code === 'KeyU' || e.code === 'Digit2') this.toggleUpgradeMenu();
      if (e.code === 'Digit1') this.toggleStatPanel();
      if (e.code === 'Escape') { this.closeUpgradeMenu(); this.closeStatPanel(); }
    });

    // Click outside stats popup → close it
    document.addEventListener('mousedown', (e) => {
      if (!this.isStatPanelOpen) return;
      const target = e.target as HTMLElement;
      if (!this.statsPopup.contains(target) && target !== this.statsBtn) {
        this.closeStatPanel();
      }
    });

    // PowerUp animation keyframes
    const puStyle = document.createElement('style');
    puStyle.textContent = `
      @keyframes powerup-banner-in {
        from { opacity:0; transform:translateX(-50%) translateY(-18px) scale(0.88); }
        to   { opacity:1; transform:translateX(-50%) translateY(0)     scale(1);    }
      }
      @keyframes powerup-pulse {
        0%,100% { filter:brightness(1)    drop-shadow(0 0  6px currentColor); }
        50%     { filter:brightness(1.45) drop-shadow(0 0 22px currentColor); }
      }
    `;
    document.head.appendChild(puStyle);
  }

  setNetwork(network: ColyseusClient) {
    this.network = network;
  }

  updateHealth(hp: number, maxHp: number) {
    if (hp === this.prevHp && maxHp === this.prevMaxHp) return;
    this.prevHp = hp;
    this.prevMaxHp = maxHp;
    const pct = Math.max(0, (hp / maxHp) * 100);
    this.healthBarFill.style.width = `${pct}%`;
    if (pct > 50) {
      this.healthBarFill.style.background = 'linear-gradient(90deg, #00e676, #69f0ae)';
    } else if (pct > 25) {
      this.healthBarFill.style.background = 'linear-gradient(90deg, #ffd54f, #ffab40)';
    } else {
      this.healthBarFill.style.background = 'linear-gradient(90deg, #ff5252, #ff8a80)';
    }
  }

  updateRewards(crystals: number, xp: number, level: number) {
    if (crystals === this.crystals && xp === this.xp && level === this.level) return;
    const crystalsChanged = crystals !== this.crystals;
    this.crystals = crystals;
    this.xp = xp;
    this.level = level;

    this.crystalCount.textContent = crystals.toLocaleString();
    this.levelDisplay.textContent = `Level ${level}`;

    const currentLevelXp = xpForLevel(level - 1);
    const nextLevelXp = xpForLevel(level);
    const progress = (xp - currentLevelXp) / (nextLevelXp - currentLevelXp);
    this.xpBarFill.style.width = `${Math.max(0, Math.min(100, progress * 100))}%`;

    // Crystal balance affects affordability display — refresh if panel is open
    if (crystalsChanged) {
      this.refreshStatPanel();
      this.updateStatDot();
    }
  }

  showRewardPopup(crystals: number, xp: number, screenX: number, screenY: number) {
    const popup = document.createElement('div');
    popup.className = 'reward-popup';
    popup.textContent = `+${crystals} 💎  +${xp} XP`;
    popup.style.left = `${screenX}px`;
    popup.style.top = `${screenY}px`;
    document.getElementById('hud')!.appendChild(popup);
    setTimeout(() => popup.remove(), 1500);
  }

  showLevelUp(newLevel: number) {
    this.levelupSub.textContent = `Level ${newLevel}`;
    this.levelupBanner.classList.remove('show');
    void this.levelupBanner.offsetWidth;
    this.levelupBanner.classList.add('show');
    setTimeout(() => this.levelupBanner.classList.remove('show'), 2500);
  }

  showDeathScreen() {
    this.deathScreen.style.opacity = '1';
  }

  hideDeathScreen() {
    this.deathScreen.style.opacity = '0';
  }

  /**
   * Flash a red vignette scaled to damage severity.
   * @param damage   HP lost this tick
   * @param maxHp    Player's max HP (for normalising intensity)
   */
  showHitFlash(damage: number, maxHp: number) {
    const intensity = Math.min(1, damage / maxHp * 4); // 25% HP loss → full flash
    clearTimeout(this.hitFlashTimer);
    // Snap to full opacity instantly (fast ease-in transition)
    this.hitFlash.style.transition = 'opacity 0.06s ease-in';
    this.hitFlash.style.opacity = String(intensity.toFixed(2));
    // Then fade out over 350 ms
    this.hitFlashTimer = window.setTimeout(() => {
      this.hitFlash.style.transition = 'opacity 0.35s ease-out';
      this.hitFlash.style.opacity = '0';
    }, 60);
  }

  showKillNotification(victimName: string) {
    const nameEl = document.getElementById('kill-banner-name')!;
    nameEl.textContent = victimName;
    // Pop in
    this.killBanner.style.transition = 'opacity 0.12s ease-out, transform 0.12s ease-out';
    this.killBanner.style.opacity = '1';
    this.killBanner.style.transform = 'translateX(-50%) scale(1)';
    // Fade out after 1.4 s
    setTimeout(() => {
      this.killBanner.style.transition = 'opacity 0.4s ease-in, transform 0.4s ease-in';
      this.killBanner.style.opacity = '0';
      this.killBanner.style.transform = 'translateX(-50%) scale(0.85)';
    }, 1400);
  }

  addKillFeedEntry(killerName: string, victimName: string) {
    const entry = document.createElement('div');
    entry.className = 'kill-entry';
    entry.innerHTML = `<span class="killer">${killerName}</span> ⚡ <span class="victim">${victimName}</span>`;
    this.killFeed.prepend(entry);
    setTimeout(() => entry.remove(), 4000);
  }

  updateKills(kills: number, deaths: number) {
    if (kills === this.prevKills && deaths === this.prevDeaths) return;
    this.prevKills = kills;
    this.prevDeaths = deaths;
    this.killsDisplay.textContent = `${kills} / ${deaths}`;
  }

  setOwnedUpgrades(owned: typeof this.ownedUpgrades) {
    this.ownedUpgrades = owned;
  }

  setStatLevels(levels: Record<string, number>) {
    // Compare against last server-confirmed values (not optimistic local state)
    // so the lock is released once the server acknowledges the change.
    let pendingCleared = false;
    for (const key of this.pendingUpgrades) {
      if ((levels[key] ?? 0) !== (this.confirmedStatLevels[key] ?? 0)) {
        this.pendingUpgrades.delete(key);
        pendingCleared = true;
      }
    }
    this.confirmedStatLevels = levels;

    // Re-render when levels changed OR when a pending lock was just released
    // (lock release must re-enable the + button even if optimistic state already matched)
    const hasChanges = pendingCleared || Object.keys(levels).some(k => levels[k] !== this.statLevels[k]);
    this.statLevels = levels;
    if (hasChanges) {
      this.refreshStatPanel();
      this.updateStatDot();
    }
  }

  // ── Upgrade modal ─────────────────────────────────────────

  toggleUpgradeMenu() {
    if (this.upgradeMenu.classList.contains('open')) {
      this.closeUpgradeMenu();
    } else {
      this.openUpgradeMenu();
    }
  }

  openUpgradeMenu() {
    this.upgradeMenu.classList.add('open');
    this.renderUpgradeMenu();
  }

  closeUpgradeMenu() {
    this.upgradeMenu.classList.remove('open');
  }

  get isMenuOpen() {
    return this.upgradeMenu.classList.contains('open');
  }

  private renderUpgradeMenu() {
    this.upgradeCrystalBalance.textContent = `💎 ${this.crystals.toLocaleString()} Crystals`;

    const categories: { key: string; label: string; items: any[] }[] = [
      { key: 'weapon',     label: '🔫 Weapon',    items: [...UPGRADE_DEFS.weapon]    },
      { key: 'chassis',    label: '🛡️ Chassis',   items: [...UPGRADE_DEFS.chassis]   },
      { key: 'movement',   label: '⚙️ Movement',  items: [...UPGRADE_DEFS.movement]  },
      { key: 'attachment', label: '🔩 Attachment', items: [...UPGRADE_DEFS.attachment]},
    ];

    this.upgradeContent.innerHTML = '';

    categories.forEach(cat => {
      const section = document.createElement('div');
      section.className = 'upgrade-category';

      const heading = document.createElement('h3');
      heading.textContent = cat.label;
      section.appendChild(heading);

      const itemsEl = document.createElement('div');
      itemsEl.className = 'upgrade-items';

      cat.items.forEach((item: any) => {
        const div = document.createElement('div');
        div.className = 'upgrade-item';

        const currentTier = cat.key === 'attachment'
          ? this.ownedUpgrades.attachment
          : (this.ownedUpgrades as any)[cat.key];

        const isOwned = cat.key === 'attachment'
          ? this.ownedUpgrades.attachment === item.id
          : currentTier >= item.id;

        const isLocked = this.level < item.levelReq;
        const cantAfford = this.crystals < item.cost;

        if (isOwned) div.classList.add('owned');
        if (isLocked) div.classList.add('locked');

        div.innerHTML = `
          <div class="item-name">${item.name}</div>
          <div class="item-desc">${item.desc}</div>
          ${item.cost > 0
            ? `<div class="item-cost ${!isOwned && cantAfford && !isLocked ? 'cant-afford' : ''}">${isOwned ? '✓ Owned' : `💎 ${item.cost}`}</div>`
            : `<div class="item-cost">Free</div>`
          }
          ${isLocked ? `<div class="item-level-req">⚡ Requires Level ${item.levelReq}</div>` : ''}
          ${isOwned ? '<span class="owned-badge">EQUIPPED</span>' : ''}
        `;

        if (!isOwned && !isLocked && !cantAfford) {
          div.addEventListener('click', () => {
            this.network?.purchaseUpgrade(cat.key, item.id);
            this.closeUpgradeMenu();
          });
        }

        itemsEl.appendChild(div);
      });

      section.appendChild(itemsEl);
      this.upgradeContent.appendChild(section);
    });
  }

  // ── Stats popup ────────────────────────────────────────────

  toggleStatPanel() {
    if (this.statsPopup.classList.contains('open')) {
      this.closeStatPanel();
    } else {
      this.openStatPanel();
    }
  }

  openStatPanel() {
    this.statsPopup.classList.add('open');
    this.refreshStatPanel();
  }

  closeStatPanel() {
    this.statsPopup.classList.remove('open');
  }

  get isStatPanelOpen() {
    return this.statsPopup.classList.contains('open');
  }

  private updateStatDot() {
    const hasAffordable = (Object.keys(STAT_UPGRADE_DEFS) as StatKey[]).some(key => {
      const level = this.statLevels[key] ?? 0;
      if (level >= STAT_MAX_LEVEL) return false;
      return this.crystals >= STAT_UPGRADE_DEFS[key].costs[level];
    });
    this.statsBtn.classList.toggle('has-upgrades', hasAffordable);
  }

  private refreshStatPanel() {
    if (!this.isStatPanelOpen) return;

    this.statsPopup.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'stat-popup-title';
    title.innerHTML = `<span>Stats</span><span class="stat-popup-crystals">💎 ${this.crystals.toLocaleString()}</span>`;

    const closeBtn = document.createElement('button');
    closeBtn.className = 'stat-popup-close';
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', () => this.closeStatPanel());
    title.appendChild(closeBtn);

    this.statsPopup.appendChild(title);

    (Object.keys(STAT_UPGRADE_DEFS) as StatKey[]).forEach(statKey => {
      const def = STAT_UPGRADE_DEFS[statKey];
      const currentLevel = this.statLevels[statKey] ?? 0;
      const isMaxed = currentLevel >= STAT_MAX_LEVEL;
      const nextLevel = currentLevel + 1;
      const cost = isMaxed ? 0 : def.costs[currentLevel];
      const cantAfford = !isMaxed && this.crystals < cost;

      const row = document.createElement('div');
      row.className = `stat-row${isMaxed ? ' maxed' : ''}`;

      const labelCol = document.createElement('div');
      labelCol.className = 'stat-label-col';
      labelCol.textContent = def.label;

      const pips = document.createElement('div');
      pips.className = 'stat-pips';
      for (let i = 0; i < STAT_MAX_LEVEL; i++) {
        const pip = document.createElement('div');
        pip.className = `stat-pip${i < currentLevel ? (isMaxed ? ' filled-max' : ' filled') : ''}`;
        pips.appendChild(pip);
      }

      const costCol = document.createElement('div');
      if (isMaxed) {
        costCol.className = 'stat-cost-col maxed-label';
        costCol.textContent = 'MAX';
      } else {
        costCol.className = `stat-cost-col${cantAfford ? ' cant-afford' : ''}`;
        costCol.textContent = `💎 ${cost}`;
      }

      const plusBtn = document.createElement('button');
      plusBtn.className = 'stat-plus-btn';
      plusBtn.textContent = '+';
      plusBtn.disabled = isMaxed || cantAfford || this.pendingUpgrades.has(statKey);
      if (!isMaxed) {
        plusBtn.addEventListener('click', () => {
          if (plusBtn.disabled) return;
          this.network?.upgradeStat(statKey, nextLevel);
          this.pendingUpgrades.add(statKey);
          // No optimistic stat/crystal mutation — server confirms within 1 tick (~50ms)
          this.refreshStatPanel();
          this.updateStatDot();
        });
      }

      row.appendChild(labelCol);
      row.appendChild(pips);
      row.appendChild(costCol);
      row.appendChild(plusBtn);

      this.statsPopup.appendChild(row);
    });
  }

  // ── PowerUp HUD ────────────────────────────────────────────

  private powerUpIndicator: HTMLDivElement | null = null;
  private powerUpTimerEl: HTMLSpanElement | null = null;
  private powerUpActiveType = '';
  private powerUpBannerTimeout: ReturnType<typeof setTimeout> | null = null;

  showPowerUpBanner(type: string) {
    const labels: Record<string, string> = {
      star:     'INVISIBILITY',
      speed:    'SPEED ×2',
      fireball: 'FIREBALL',
      iceball:  'ICE BALL',
      shield:   'SHIELD',
    };
    const colors: Record<string, string> = {
      star:     '#ffe600',
      speed:    '#00ccff',
      fireball: '#ff6600',
      iceball:  '#4db8ff',
      shield:   '#66ff66',
    };
    const label = labels[type] ?? type.toUpperCase();
    const color = colors[type] ?? '#ffffff';

    // Remove any existing banner
    document.getElementById('powerup-banner')?.remove();

    const banner = document.createElement('div');
    banner.id = 'powerup-banner';
    banner.textContent = `⚡ ${label} — ACTIVE`;
    banner.style.cssText = [
      'position:fixed',
      'top:22%',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:20',
      'padding:7px 20px',
      'border-radius:8px',
      `color:${color}`,
      `border:2px solid ${color}`,
      'background:rgba(0,0,0,0.42)',
      'font-size:14px',
      'font-weight:900',
      'letter-spacing:0.15em',
      'text-transform:uppercase',
      'pointer-events:none',
      'animation:powerup-banner-in 0.25s ease-out',
      `text-shadow:0 0 12px ${color}`,
      `box-shadow:0 0 18px ${color}44`,
    ].join(';');

    document.getElementById('hud')?.appendChild(banner) ?? document.body.appendChild(banner);

    if (this.powerUpBannerTimeout) clearTimeout(this.powerUpBannerTimeout);
    this.powerUpBannerTimeout = setTimeout(() => banner.remove(), 2500);
  }

  updatePowerUpTimer(type: string, secs: number) {
    if (!type || secs <= 0) {
      this.powerUpIndicator?.remove();
      this.powerUpIndicator = null;
      this.powerUpTimerEl = null;
      this.powerUpActiveType = '';
      return;
    }

    const colors: Record<string, string> = {
      star:     '#ffe600',
      speed:    '#00ccff',
      fireball: '#ff6600',
      iceball:  '#4db8ff',
      shield:   '#66ff66',
    };
    const icons: Record<string, string> = {
      star: '★', speed: '⚡', fireball: '🔥', iceball: '❄', shield: '🛡',
    };
    const labels: Record<string, string> = {
      star: 'INVISIBILITY', speed: 'SPEED ×2', fireball: 'FIREBALL', iceball: 'ICE BALL', shield: 'SHIELD',
    };
    const color  = colors[type]  ?? '#ffffff';
    const icon   = icons[type]   ?? '?';
    const label  = labels[type]  ?? type.toUpperCase();

    // Rebuild if type changed or first creation
    if (!this.powerUpIndicator || this.powerUpActiveType !== type) {
      this.powerUpIndicator?.remove();
      this.powerUpActiveType = type;

      this.powerUpIndicator = document.createElement('div');
      this.powerUpIndicator.id = 'powerup-indicator';
      this.powerUpIndicator.style.cssText = [
        'position:fixed',
        'top:80px',
        'left:50%',
        'transform:translateX(-50%)',
        'z-index:15',
        'display:flex',
        'align-items:center',
        'gap:14px',
        'padding:7px 22px',
        'border-radius:40px',
        'background:rgba(0,0,0,0.42)',
        'border:2px solid currentColor',
        'font-size:14px',
        'font-weight:900',
        'letter-spacing:0.1em',
        'text-transform:uppercase',
        'pointer-events:none',
        'backdrop-filter:blur(10px)',
        'animation:powerup-pulse 1s ease-in-out infinite',
        'white-space:nowrap',
      ].join(';');

      this.powerUpIndicator.innerHTML = `
        <span style="font-size:1.2em;line-height:1">${icon}</span>
        <span style="opacity:0.92;letter-spacing:0.12em">${label}</span>
        <span style="opacity:0.35;font-weight:300;font-size:0.85em">|</span>
        <span id="pu-timer" style="min-width:2.4em;text-align:left"></span>
      `;
      this.powerUpTimerEl = this.powerUpIndicator.querySelector('#pu-timer');
      document.getElementById('hud')?.appendChild(this.powerUpIndicator)
        ?? document.body.appendChild(this.powerUpIndicator);
    }

    this.powerUpIndicator.style.color = color;
    this.powerUpIndicator.style.textShadow = `0 0 12px ${color}`;
    this.powerUpIndicator.style.boxShadow  = `0 0 24px ${color}33`;
    this.powerUpIndicator.style.borderColor = color;
    const ceilSecs = Math.ceil(secs);
    if (this.powerUpTimerEl && this.powerUpTimerEl.textContent !== `${ceilSecs}s`) {
      this.powerUpTimerEl.textContent = `${ceilSecs}s`;
    }
  }

  clearPowerUpBanner() {
    document.getElementById('powerup-banner')?.remove();
    this.powerUpIndicator?.remove();
    this.powerUpIndicator = null;
    this.powerUpTimerEl = null;
    this.powerUpActiveType = '';
  }

  // ── FFA match timer ────────────────────────────────────────

  private ffaTimerEl: HTMLDivElement | null = null;

  showFFATimer() {
    if (this.ffaTimerEl) return;
    this.ffaTimerEl = document.createElement('div');
    this.ffaTimerEl.id = 'ffa-timer';
    this.ffaTimerEl.style.cssText = [
      'position:fixed',
      'top:10px',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:15',
      'padding:5px 18px',
      'border-radius:6px',
      'background:rgba(0,0,0,0.55)',
      'border:1px solid rgba(255,255,255,0.12)',
      'color:#ddd',
      'font-size:15px',
      'font-weight:700',
      'font-variant-numeric:tabular-nums',
      'letter-spacing:0.05em',
      'pointer-events:none',
      'backdrop-filter:blur(8px)',
    ].join(';');
    this.ffaTimerEl.textContent = '10:00';
    document.body.appendChild(this.ffaTimerEl);
  }

  updateFFATimer(secondsLeft: number) {
    if (!this.ffaTimerEl) return;
    const m = Math.floor(secondsLeft / 60);
    const s = Math.floor(secondsLeft % 60);
    this.ffaTimerEl.textContent = `${m}:${s.toString().padStart(2, '0')}`;
    this.ffaTimerEl.style.color = secondsLeft <= 60 ? '#ff6666' : '#ddd';
  }

  showFFAMatchEnd(winner: string, topKills: number, returnTarget = 'lobby'): Promise<void> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = `
        position: fixed; inset: 0; background: rgba(0,0,0,0.78);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        z-index: 300; font-family: 'Segoe UI', sans-serif; color: #fff;
      `;
      const title = document.createElement('div');
      title.textContent = winner === 'draw' ? 'DRAW!' : `${winner} WINS!`;
      title.style.cssText = `
        font-size: 56px; font-weight: 900; letter-spacing: 4px;
        color: ${winner === 'draw' ? '#ddd' : '#ffaa00'};
        text-shadow: 0 0 30px currentColor;
      `;
      const stats = document.createElement('div');
      stats.textContent = winner === 'draw' ? 'No clear winner' : `${topKills} kills`;
      stats.style.cssText = 'font-size: 28px; font-weight: 700; margin-top: 16px; color: #ccc;';
      const sub = document.createElement('div');
      sub.style.cssText = 'font-size: 16px; color: #888; margin-top: 24px;';
      overlay.appendChild(title);
      overlay.appendChild(stats);
      overlay.appendChild(sub);
      document.body.appendChild(overlay);

      let secs = 6;
      const tick = () => {
        sub.textContent = `Returning to ${returnTarget} in ${secs}\u2026`;
        if (secs > 0) { secs--; setTimeout(tick, 1000); }
        else { overlay.remove(); resolve(); }
      };
      tick();
    });
  }
}
